import { useEffect, useState } from 'react';
import { api, darf, datum, zeitpunkt, type Anmelder } from '../api.ts';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage }
  from '../teile/Formular.tsx';
/* DIE NUMMER WIRD NICHT ZUM ZWEITEN MAL GEKUERZT. `versionsnummer` steht in
   `teile/version.ts` und schneidet aus „2026-08-26.115" die 115 heraus - sie
   steht auch unten links in der Fussleiste. Eine zweite Fassung liefe beim
   ersten Nachtrag auseinander, und dann heisst dieselbe Version auf zwei
   Bildschirmen verschieden. */
import { versionsnummer } from '../teile/version.ts';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Was in welcher Version gemacht wurde - und wo nachgearbeitet werden muss.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „baue außerdem eine changelog seite, wo du festhälfst was wir gemacht
 *    haben, damit ich ggf. zu einem changelogpunkt zurückspringen kann wenn du
 *    es nicht richtig umgesetzt hast und nachgearbeitet werden muss"
 *
 * ZWEI DINGE, UND DAS ZWEITE IST DER GRUND FÜR DAS ERSTE. Zum Lesen gäbe es
 * schon einen Text - er steht in `lieferung.json` und wird beim Start
 * ausgelesen. Was fehlte, ist ein HANDGRIFF: eine Kennung je Punkt, die man
 * nennen kann, und ein Knopf daneben. Ohne den bleibt die Rückmeldung ein
 * Satz in einer Nachricht („bild 3 war nicht richtig"), und beim dritten Mal
 * weiss niemand mehr, welcher Punkt gemeint war.
 *
 * DIE OFFENEN PUNKTE STEHEN OBEN. Ein Verzeichnis, in dem man die Nacharbeit
 * suchen muss, ist eines, in dem sie liegen bleibt.
 *
 * UND SEIT .116 IST ES EINE LISTE UND KEIN AUFSATZ.
 *
 *   ORIGINALTEXT MARVIN (05.09.2026)
 *   > „die changelog soll in stichpunkten aufgeführt sein zu jedem
 *   >  versionsänderung und das datum, wann es eingespielt wurde soll da
 *   >  stehen, neben den fehlerbehebungen soll dann auch bei den jeweiligen
 *   >  relases auch neue featurees stichpunktartig genannt sein"
 *
 * **DER TITEL IST DER STICHPUNKT.** Eine Version trug bis dahin fünf Blöcke
 * aus Überschrift, Zitat und einem Absatz Fliesstext - zusammen eine
 * Bildschirmhöhe je Punkt, und wer wissen will, was eine Lieferung gebracht
 * hat, liest das nicht. Jetzt steht je Punkt EINE Zeile; die Erklärung klappt
 * auf, wenn jemand sie wirklich will.
 *
 * **UND DER TEXT WIRD DAFÜR NICHT UMGESCHRIEBEN.** „Ein Punkt wird nie
 * gelöscht und nie umgeschrieben" gilt weiter - was sich ändert, ist die
 * ANSICHT. Was sich einordnet, ist die neue Spalte `art` daneben.
 */
export default function Changelog({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [melden, setMelden] = useState<any>(null);
  const [antwort, setAntwort] = useState<string | null>(null);
  const [nurOffen, setNurOffen] = useState(false);

  const darfMelden = darf(ich, 'changelog.melden' as any);

  function laden() {
    api.changelog().then(setD).catch((f) => setFehler(ausFehler(f)));
  }
  useEffect(laden, []);
  useFrisch(laden);

  if (!d) return <p className="klein leise">Das Verzeichnis wird geladen.</p>;

  const versionen = nurOffen
    ? d.versionen
        .map((v: any) => ({ ...v, punkte: v.punkte.filter((p: any) => p.nacharbeit) }))
        .filter((v: any) => v.punkte.length)
    : d.versionen;

  return (
    <div className="inhalt">
      <div className="kopfzeile">
        <h1>Änderungen</h1>
        <div className="wachsen" />
        {/* EIN FILTER, DER IMMER NULL TRIFFT, STEHT GAR NICHT ERST DA. */}
        {d.offen > 0 && (
          <button className="knopf" aria-current={nurOffen}
                  onClick={() => setNurOffen(!nurOffen)}>
            {nurOffen ? 'Alle Punkte' : `Nur Nacharbeit (${d.offen})`}
          </button>
        )}
      </div>

      <Fehlerkasten lage={fehler} />
      {antwort && <div className="hinweis erledigt">{antwort}</div>}

      <p className="klein leise">
        Jeder Punkt hat eine Kennung wie <code>86-4</code>. Sie ist dafür da,
        genannt zu werden: „86-4 ist nicht richtig umgesetzt" sagt genau, was
        gemeint ist.
        {darfMelden
          ? ' Der Knopf daneben hält denselben Satz im System fest.'
          : ' Den Vermerk setzt, wer das Recht „Nacharbeit melden" hat.'}
      </p>

      {versionen.length === 0 && (
        <div className="karte"><div className="leib">
          <p className="leise" style={{ margin: 0 }}>
            {nurOffen ? 'Kein Punkt ist als nachzuarbeiten vermerkt.'
                      : 'Es steht noch nichts im Verzeichnis.'}
          </p>
        </div></div>
      )}

      {versionen.map((v: any, i: number) => (
        <Versionskarte key={v.version} v={v} offenAb={i === 0}
                       darfMelden={darfMelden} melde={setMelden} />
      ))}

      {melden && (
        <Nacharbeit
          p={melden} zu={() => setMelden(null)}
          fertig={(satz: string) => {
            setAntwort(satz); setMelden(null); laden();
          }}
          setzeFehler={setFehler} />
      )}
    </div>
  );
}

/**
 * Die vier Arten, mit ihrer Aufschrift und ihrer Farbe.
 *
 * Sie stehen HIER und nicht im Server: welche Farbe eine Marke trägt, ist
 * eine Sache der Anzeige. WELCHE Arten es gibt, entscheidet die Datenbank
 * (`changelog_art_check`), und eine unbekannte wird nicht verschluckt -
 * sie steht mit ihrem rohen Wert da, wie jeder Aufzählungswert in diesem
 * Haus. Ein leeres Feld fällt nicht auf.
 */
const ART: Record<string, { text: string; marke: string }> = {
  neu:       { text: 'Neu',      marke: 'gruen' },
  geaendert: { text: 'Geändert', marke: 'blau' },
  behoben:   { text: 'Behoben',  marke: '' },
  geprueft:  { text: 'Geprüft',  marke: 'leise' },
};
/** Die Reihenfolge in der Kopfzeile - dieselbe wie in der Liste darunter. */
const ARTEN = ['neu', 'geaendert', 'behoben', 'geprueft'];

/**
 * EINE VERSION, EINE LISTE.
 *
 * Der Kopf sagt in einer Zeile, was diese Lieferung gebracht hat: wann sie
 * eingespielt wurde und wie viele Punkte je Art. **Eine Zahl, die immer null
 * ist, steht gar nicht erst da** - dieselbe Regel wie bei den Kacheln der
 * Auswertung und beim Filter „neu" der Arbeitsliste.
 *
 * DIE NEUESTE VERSION STEHT OFFEN, die älteren zugeklappt. Wer diese Seite
 * aufmacht, will fast immer wissen, was zuletzt passiert ist; einunddreissig
 * offene Kästen sind wieder die Wand, die es zu vermeiden gilt.
 */
function Versionskarte({ v, offenAb, darfMelden, melde }: any) {
  const [offen, setOffen] = useState<boolean>(offenAb);
  const nacharbeit = v.punkte.filter((p: any) => p.nacharbeit).length;

  return (
    <div className="karte">
      <h3>
        <button type="button" className="klappe" aria-expanded={offen}
                onClick={() => setOffen(!offen)}>
          <span className="pfeil">{offen ? '▾' : '▸'}</span>
          Version {versionsnummer(v.version)}
        </button>
        {/* DAS EINSPIELDATUM IST GEMESSEN UND NICHT BEHAUPTET: es ist der
            Zeitpunkt, an dem die Migration dieser Version auf DIESEM Server
            gelaufen ist. `stand` daneben ist das Datum der Lieferung.

            UND DIE AUFSCHRIFT HEISST HIER „RELEASE DATUM".
            ORIGINALTEXT MARVIN (05.09.2026)
              „«eingespielt am» schreibst du um in «Release Datum»"
            Sie stand bis .115 NUR unten links in der Fussleiste - also an der
            einen Stelle im Haus, an der dafuer kein Platz ist: 219 Bildpunkte
            Text in einer Spalte von 207, nachgemessen im Browser, und der Rest
            fiel unter drei Punkte. **Eine Aufschrift gehoert dorthin, wo sie
            hinpasst**; hier ist die Zeile so breit wie die Karte. */}
        <span className="weg">
          {v.eingespielt
            ? `Release Datum ${datum(v.eingespielt)}`
            : v.stand ? datum(v.stand) : ''}
        </span>
        {/* UND DIE VOLLE KENNUNG STEHT DANEBEN.
            Sie stand bis .117 nur im `title` der Version unten links, und den
            hat Marvin am 06.09.2026 wegnehmen lassen („die hover mit deb infos
            soll weg"). **Wer einen Hover abschafft, sieht nach, was darin
            stand** - hier war es die Kennung, mit der man eine Version meldet
            und die in `lieferung.json` steht. Ein Titel war ohnehin die
            schlechtere Stelle: auf dem Telefon gibt es ihn nicht, und
            kopieren lässt er sich auch nicht. */}
        <span className="weg einfarbig">{v.version}</span>
        <div className="wachsen" />
        {ARTEN.filter((a) => v.zahl?.[a]).map((a) => (
          <span key={a} className={'merker ' + (ART[a]?.marke ?? '')}>
            {v.zahl[a]} {ART[a]?.text ?? a}
          </span>
        ))}
        {nacharbeit > 0 && (
          <span className="merker warnung">{nacharbeit} Nacharbeit</span>
        )}
      </h3>

      {offen && (
        <div className="leib" style={{ padding: 0 }}>
          {v.punkte.map((p: any) => (
            <Punktzeile key={p.punkt} p={p} darfMelden={darfMelden}
                        melde={melde} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * EIN PUNKT IST EINE ZEILE.
 *
 * Kennung, Art, Titel - mehr steht nicht da, bis jemand aufklappt. Die
 * Erklärung und Marvins Wortlaut sind dann darunter.
 *
 * **EINE OFFENE NACHARBEIT STEHT IMMER OFFEN.** Ein Vermerk, den man
 * aufklappen muss, um ihn zu sehen, ist einer, der liegen bleibt - und genau
 * dafür gibt es diese Seite.
 */
function Punktzeile({ p, darfMelden, melde }: any) {
  const [auf, setAuf] = useState<boolean>(!!p.nacharbeit);
  const a = ART[p.art] ?? { text: p.art, marke: '' };

  return (
    <div className={'changelogpunkt' + (p.nacharbeit ? ' offen' : '')}>
      <div className="reihe">
        <code className="punktkennung">{p.punkt}</code>
        <span className={'merker ' + a.marke}>{a.text}</span>
        <button type="button" className="klappe wachsen" aria-expanded={auf}
                onClick={() => setAuf(!auf)}>
          <b>{p.titel}</b>
        </button>
        {p.nacharbeit && <span className="merker warnung">Nacharbeit</span>}
        {/* NUR FÜR DEN, DER SIE LESEN DARF. Der Server schickt `vertraulich`
            gar nicht erst mit, wenn das Recht fehlt - ein Schild an der Tür
            wäre eine Einladung, nach dem Inhalt zu fragen. */}
        {p.vertraulich && (
          <span className="merker warnung"
                title={'Der Wortlaut dieses Punktes geht nicht an jeden, '
                     + 'der das Verzeichnis liest.'}>nur intern</span>
        )}
        {darfMelden && (
          <button className="knopf klein" onClick={() => melde(p)}>
            {p.nacharbeit ? 'erledigt' : 'nacharbeiten'}
          </button>
        )}
      </div>

      {auf && (<>
        {/* MARVINS WORTLAUT STEHT DABEI. Ohne ihn ist nach ein paar Tagen
            nicht mehr erkennbar, welcher Punkt gemeint war. */}
        {p.original && (
          <blockquote className="original">{p.original}</blockquote>
        )}
        <p className="klein" style={{ whiteSpace: 'pre-wrap' }}>{p.text}</p>
        {!!(p.betrifft ?? []).length && (
          <p className="klein leise" style={{ margin: 0 }}>
            Betrifft: {p.betrifft.join(', ')}
          </p>
        )}
      </>)}

      {p.nacharbeit && (
        <div className="hinweis warnung" style={{ marginTop: 8 }}>
          <b>Nacharbeit nötig.</b> {p.nacharbeit_grund}
          <div className="klein" style={{ fontWeight: 400 }}>
            {p.nacharbeit_name}, {zeitpunkt(p.nacharbeit_am)}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Der Vermerk.
 *
 * DER GRUND IST PFLICHT, wenn er GESETZT wird, und beim Zurücknehmen nicht.
 * Ein Haken ohne Satz ist am nächsten Tag nicht mehr zu deuten; „erledigt"
 * braucht dagegen keine Begründung.
 */
function Nacharbeit({ p, zu, fertig, setzeFehler }: any) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const zurueck = !!p.nacharbeit;

  async function tu() {
    setLaeuft(true);
    try {
      const v = await api.changelogNacharbeit(p.punkt,
        { noetig: !zurueck, grund });
      fertig(v.hinweis);
    } catch (f) { setzeFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel={zurueck ? 'Vermerk zurücknehmen' : 'Nacharbeit vermerken'}
            untertitel={`${p.punkt}, ${p.titel}`}
            laeuft={laeuft} zu={zu} breite={620}>
      {zurueck ? (
        <p className="klein">
          Der Vermerk verschwindet aus der Liste. Der Punkt selbst bleibt
          stehen: er beantwortet weiter die Frage, was in dieser Version
          gemacht wurde.
        </p>
      ) : (
        <Text name="grund" titel="Woran hakt es?" pflicht mehrzeilig
              wert={grund} setze={setGrund}
              hinweis="Ein Satz genügt. Er steht danach an diesem Punkt." />
      )}
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || (!zurueck && !grund.trim())}
                onClick={tu}>
          {laeuft ? 'Einen Moment' : zurueck ? 'Zurücknehmen' : 'Vermerken'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
